/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback } from 'react'
import useEmblaCarousel from 'embla-carousel-react'

interface Breakpoint {
  query: string
  options: any
  plugins?: any
}

interface UseEmblaCarouselResponsiveProps {
  options: any
  plugins?: any
  breakpoints?: Breakpoint[]
}

export function useEmblaCarouselResponsive({
  options,
  plugins = [],
  breakpoints = [],
}: UseEmblaCarouselResponsiveProps) {
  const [resolvedOptions, setResolvedOptions] = useState(options)
  const [resolvedPlugins, setResolvedPlugins] = useState(plugins)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel(resolvedOptions, resolvedPlugins)

  // Handle responsive settings
  useEffect(() => {
    const mediaQueries = breakpoints.map(breakpoint => ({
      queryList: window.matchMedia(breakpoint.query),
      breakpoint,
    }))
  
    const applyResponsiveSettings = () => {
      let currentOptions = options
      let currentPlugins = plugins
      for (const { queryList, breakpoint } of mediaQueries) {
        if (queryList.matches) {
          currentOptions = breakpoint.options
          currentPlugins = breakpoint.plugins || []
        }
      }

      setResolvedOptions(currentOptions)
      setResolvedPlugins(currentPlugins)
    }
  
    applyResponsiveSettings()
  
    mediaQueries.forEach(({ queryList }) => queryList.addEventListener('change', applyResponsiveSettings))
  
    return () => {
      mediaQueries.forEach(({ queryList }) =>
        queryList.removeEventListener('change', applyResponsiveSettings)
      )
    }
  }, [])
  

  const updateScrollButtons = useCallback(() => {
    if (!emblaApi) return
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    updateScrollButtons()
    emblaApi.on('select', updateScrollButtons)
    emblaApi.on('reInit', updateScrollButtons)
    return () => {
      emblaApi.off('select', updateScrollButtons)
      emblaApi.off('reInit', updateScrollButtons)
    }
  }, [emblaApi, updateScrollButtons])

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  return { emblaRef, canScrollPrev, canScrollNext, selectedIndex, emblaApi, scrollPrev, scrollNext }
}
